import { RoundedFilledButton } from '@/components/buttons/Buttons';
import LoadingBlocker from '@/components/dialog/LoadingBlocker';
import CheckboxInput from '@/components/form-inputs/CheckboxInput';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import BillingInfoForm, { BillingFormLayoutType, BillingInfoFormRef } from '../components/forms/BillingInfoForm';
import styles from './add-billing-info-form-dialog.module.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createTypedModal, useTypedModal } from '@/common/common_utils';
import { OnboardingStep } from '@/types';
import { cn } from '@/components/ui/cn';
import { BillingApi, BillingInfoDto } from '../billing.api';
import { MdClose } from 'react-icons/md';
import { TextHeadlineLarge, TextLabelExtraSmall } from '@/components/text/Texts';
import { Dialog } from 'primereact/dialog';
import { useOnboardingState } from '@/modules/onboarding/engine/onboarding.hooks';

const AddBillingInfoFormModal = createTypedModal<{ onInfoAdded?: () => void }, undefined>(({ onInfoAdded }) => {
	const modal = useTypedModal();
	const queryClient = useQueryClient();
	const [formBlocked, setFormBlocked] = useState(false);
	const [consentChecked, setConsentChecked] = useState(false);
	const { completeStepMutation } = useOnboardingState();
	const formId = 'add-billing-info';

	const form = useForm();

	const formRef = useRef<BillingInfoFormRef>(null);
	const [isFormDirty, setIsFormDirty] = useState(false);

	const billingInfoQ = useQuery({
		queryKey: ['AddBillingInfoFormModal.billingInfo'],
		queryFn: BillingApi.getBillingInfo
	});

	const onSubmit = async (data: BillingInfoDto) => {
		setFormBlocked(true);
		try {
			await BillingApi.updateBillingInfo(data);
			await completeStepMutation.mutateAsync(OnboardingStep.BillingInformation);

			// Invalidate both this modal's query and the parent modal's query
			await queryClient.invalidateQueries({ queryKey: ['AddBillingInfoFormModal.billingInfo'] });
			await queryClient.invalidateQueries({ queryKey: ['AddBillingInfoModal.billingInfo'] });

			// Call the callback if provided
			onInfoAdded?.();

			modal.resolveRemove();
		}
		finally {
			setFormBlocked(false);
		}
	};

	return (
		<Dialog
			unstyled
			onHide={() => modal.resolveRemove()}
			visible={true}
			draggable={false}
			maskStyle={{ backgroundColor: '#00000047' }}
			closeIcon={(<MdClose size={24} className="text-onSurfaceVariant rounded-full duration-200 hover:bg-gray-200" />)}
			pt={{
				root: { className: 'px-8 py-[52px] rounded-[28px] w-[544px] tablet:!max-w-[calc(100vw-6rem)] bg-white relative' },
				content: { className: 'p-0' },
				header: { className: 'absolute right-6 top-6' }
			}}
		>
			<LoadingBlocker blocked={formBlocked} blockTemplate="Adding Billing Information...">
				<TextHeadlineLarge className="mb-10 text-neutral17" text="Add Billing Information" />

				<BillingInfoForm
					ref={formRef}
					formName="add-billing-info"
					initialData={billingInfoQ.data}
					onSubmit={onSubmit}
					layoutType={BillingFormLayoutType.Onboarding}
					onDirtyChange={setIsFormDirty}
				/>

				<CheckboxInput
					containerClassName={styles.checkboxContainer}
					id="consent"
					control={form.control}
					formName={formId}
					textEl={(
						<TextLabelExtraSmall
							text="I confirm that the information I have provided to Xpress regarding the tax situation of my business is correct"
							className="text-black align-middle"
						/>
					)}
					checked={consentChecked}
					onChange={(e) => setConsentChecked(e.checked ?? false)}
				/>

				<RoundedFilledButton
					id="add-billing-address"
					label="Add Billing Address"
					className={cn(styles.addBillingButton, "text-white h-[48px]")}
					onClick={() => formRef.current?.submit()}
					disabled={!consentChecked || !isFormDirty}
				/>
			</LoadingBlocker>
		</Dialog>
	);
});

export default AddBillingInfoFormModal;
